import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import React, { useMemo, useRef, useState } from 'react';
import { IProduct } from '../../../../types/types';
import { itemDescriptionTemplate } from './cell-templates/item-description-template';
import { itemPriceTemplate } from './cell-templates/item-price-template';
import { itemIndexTemplate } from './cell-templates/item-index-template';
import { itemNameTemplate } from './cell-templates/item-name-template';
import { itemTotalPriceTemplate } from './cell-templates/item-total-price-template';
import { itemMenuTemplate } from './cell-templates/item-menu-template';
import { itemQuantityTemplate } from './cell-templates/item-quanity-template';
import { ProductList } from '../../TableComponents/CellComponents/ProductList';
import { useStore } from '../../../../../../../store';

interface IProductsSectionTableProps {
  autocompleteResults: IProduct[];
  products: IProduct[];
  onDeleteClick: (index: number) => void;
  onEditClick: (index: number) => void;
  onProductSelected: (selectedProduct: IProduct, index: number) => void;
  onCreateNewProductClick: (index: number) => void;
}

export const ProductsTable = ({
  products,
  autocompleteResults,
  onDeleteClick,
  onEditClick,
  onProductSelected,
  onCreateNewProductClick,
}: IProductsSectionTableProps) => {
  const shouldTriggerItemsValidation = useStore((state) => state.shouldTriggerItemsValidation);
  const temporaryItemsBeingSaved = useStore((state) => state.temporaryItemsBeingSaved);
  const [isProductListShown, setIsProductListShown] = useState<boolean>(false);
  const [selectedResultIndex, setSelectedResultIndex] = useState<number>(-1);
  const [keyword, setKeyword] = useState<string>('');
  const [currentNameInputTarget, setCurrentNameInput] = useState<HTMLInputElement | null>(null);
  const headerStyle = 'text-[14px] text-bw07 font-metricR h-[40px]  border-bw03 bg-bw04 ';
  const columnClassName = 'border-l border-r last:border-r-0 first:border-l-0 border-bw03 p-0 text-center';
  const headerCellBackgroundColor = '#FBFBFD';
  const tableWrapper = useRef<HTMLDivElement>(null);
  const dropdownAnchorPoint = useMemo(() => {
    if (!tableWrapper.current || !currentNameInputTarget) return null;
    const { x: targetX, y: targetY, height: targetHeight } = currentNameInputTarget.getBoundingClientRect();
    const { x, y } = tableWrapper.current.getBoundingClientRect();
    return { posX: targetX - x - 1, posY: targetY - y + targetHeight - 2 };
  }, [currentNameInputTarget]);
  const isProductNameValid = (product: IProduct) => product.name.length > 0;
  const isProductPriceValid = (product: IProduct) => product.amount >= 0;
  const onProductCreationClick = () => {
    setIsProductListShown(false);
    onCreateNewProductClick(selectedResultIndex);
  };

  const handleDeleteServiceItem = (index: number) => {
    setIsProductListShown(false);
    onDeleteClick(index);
  };
  return (
    <div className='relative rounded-[8px] border-bw03  border-[1px]' ref={tableWrapper}>
      {isProductListShown && (
        <div
          style={{ transform: `translate(${dropdownAnchorPoint?.posX}px,${dropdownAnchorPoint?.posY}px)` }}
          className='absolute z-[999] flex flex-col w-[280px]'
        >
          <ProductList
            onProductCreation={onProductCreationClick}
            products={autocompleteResults}
            selectedProductsIds={products.map((p) => p.serviceItemId || '') || []}
            autoAddItemId=''
            onProductSelection={(product) => {
              setIsProductListShown(false);
              onProductSelected(product, selectedResultIndex);
            }}
            keyword={keyword}
            onClose={() => setIsProductListShown(false)}
          />
        </div>
      )}
      <DataTable id='data-table-prime-react-products' value={products} scrollable={false} className='p-0 m-0 '>
        <Column
          headerClassName={`${headerStyle} text-center w-[42px] bg-action03 border`}
          headerStyle={{ backgroundColor: headerCellBackgroundColor, border: '1px', borderColor: '#DFE2EB' }}
          className={columnClassName}
          field='index'
          header='#'
          sortField='index'
          body={(product, options) => itemIndexTemplate(options.rowIndex)}
        />
        <Column
          headerClassName={`${headerStyle} text-center w-[280px]`}
          headerStyle={{ backgroundColor: headerCellBackgroundColor }}
          className={columnClassName}
          field='name'
          header='Item Name'
          sortField='name'
          body={(product, options) =>
            itemNameTemplate(
              (isFocused, target) => {
                setCurrentNameInput(target);
                setKeyword(target.value);
                setSelectedResultIndex(options.rowIndex);
                setIsProductListShown(isFocused);
              },
              product,
              shouldTriggerItemsValidation && !isProductNameValid(product),
              (item) => {
                setKeyword(item.name);
                onProductSelected(item, options.rowIndex);
              },
            )}
        />
        <Column
          headerClassName={headerStyle}
          headerStyle={{ backgroundColor: headerCellBackgroundColor }}
          className={columnClassName}
          field='description'
          header='Item Description'
          body={(product, options) =>
            itemDescriptionTemplate(product, (item) => {
              onProductSelected(item, options.rowIndex);
            })}
        />
        <Column
          headerClassName={`${headerStyle}  w-[72px] text-center`}
          headerStyle={{ backgroundColor: headerCellBackgroundColor }}
          className={columnClassName}
          field='quantity'
          header='Quantity'
          body={(product, options) =>
            itemQuantityTemplate(product, (item) => {
              onProductSelected(item, options.rowIndex);
            })}
        />

        <Column
          headerClassName={`${headerStyle} w-[90px]`}
          headerStyle={{ marginRight: '48px', backgroundColor: headerCellBackgroundColor }}
          style={{ padding: '0px', margin: '0px' }}
          className={columnClassName}
          field='price'
          header='Unit Price ($)'
          body={(product, options) =>
            itemPriceTemplate(product, shouldTriggerItemsValidation && !isProductPriceValid(product), (item) => {
              onProductSelected(item, options.rowIndex);
            })}
        />
        <Column
          headerClassName={`${headerStyle} m-[48px] pl-[40px]  w-[124px]`}
          headerStyle={{ backgroundColor: headerCellBackgroundColor }}
          className={`${columnClassName}`}
          field='price'
          header='Total'
          body={(product) => itemTotalPriceTemplate(product.amount * product.quantity, product.service)}
        />
        <Column
          headerClassName={`${headerStyle} w-[84px]`}
          headerStyle={{ backgroundColor: headerCellBackgroundColor }}
          className={columnClassName}
          field='menu'
          header=''
          body={(product, options) =>
            itemMenuTemplate(
              product,
              options.rowIndex,
              product.temporaryId && temporaryItemsBeingSaved[product.temporaryId],
              handleDeleteServiceItem,
              onEditClick,
              products.length > 1 || product.name.length > 0 || product.amount >= 0,
            )}
        />
      </DataTable>
    </div>
  );
};
// || (product.name.length > 0 || product.amount > 0)
